import { createContext, useContext, useState, ReactNode } from 'react';

export interface AppointmentData {
  id: string;
  petId: string;
  petName: string;
  petSpecies: 'dog' | 'cat';
  professionalType: string;
  professionalName: string;
  professionalContact?: string;
  service: string;
  date: Date;
  duration: number; // in minutes
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'pending-payment';
  location: string;
  notes?: string;
  paymentStatus: 'paid' | 'pending' | 'refunded';
  createdAt: Date;
}

interface AppointmentsContextType {
  appointments: AppointmentData[];
  addAppointment: (appointment: AppointmentData) => void;
  updateAppointment: (id: string, updates: Partial<AppointmentData>) => void;
  cancelAppointment: (id: string) => void;
  getAppointmentsByPet: (petId: string) => AppointmentData[];
  getAllAppointments: () => AppointmentData[];
  getUpcomingAppointments: () => AppointmentData[];
}

const AppointmentsContext = createContext<AppointmentsContextType | undefined>(undefined);

export function AppointmentsProvider({ children }: { children: ReactNode }) {
  // Initialize with mock data for demonstration
  const today = new Date();
  const [appointments, setAppointments] = useState<AppointmentData[]>([
    {
      id: 'apt-1',
      petId: 'pet-1',
      petName: 'Max',
      petSpecies: 'dog',
      professionalType: 'Veterinarian',
      professionalName: 'Dr. Sarah Johnson',
      professionalContact: '+1 (555) 123-4567',
      service: 'Annual Checkup',
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2, 10, 0),
      duration: 60,
      status: 'confirmed',
      location: 'Happy Paws Veterinary Clinic',
      notes: 'Annual wellness exam and vaccinations',
      paymentStatus: 'paid',
      createdAt: new Date()
    },
    {
      id: 'apt-2',
      petId: 'pet-2',
      petName: 'Luna',
      petSpecies: 'cat',
      professionalType: 'Groomer',
      professionalName: 'Pampered Paws Grooming',
      professionalContact: '+1 (555) 234-5678',
      service: 'Full Grooming Package',
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 5, 14, 30),
      duration: 90,
      status: 'scheduled',
      location: 'Pampered Paws Salon',
      notes: 'Bath, haircut, and nail trim',
      paymentStatus: 'pending',
      createdAt: new Date()
    },
    {
      id: 'apt-3',
      petId: 'pet-1',
      petName: 'Max',
      petSpecies: 'dog',
      professionalType: 'Veterinarian',
      professionalName: 'Dr. Michael Chen',
      professionalContact: '+1 (555) 345-6789',
      service: 'Vaccination',
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7, 9, 0),
      duration: 30,
      status: 'confirmed',
      location: 'City Animal Hospital',
      notes: 'Rabies vaccine booster',
      paymentStatus: 'paid',
      createdAt: new Date()
    },
    {
      id: 'apt-4',
      petId: 'pet-2',
      petName: 'Luna',
      petSpecies: 'cat',
      professionalType: 'Veterinarian',
      professionalName: 'Dr. Sarah Johnson',
      professionalContact: '+1 (555) 123-4567',
      service: 'Dental Cleaning',
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 10, 11, 0),
      duration: 120,
      status: 'scheduled',
      location: 'Happy Paws Veterinary Clinic',
      notes: 'Professional dental cleaning under sedation',
      paymentStatus: 'pending',
      createdAt: new Date()
    },
    {
      id: 'apt-5',
      petId: 'pet-1',
      petName: 'Max',
      petSpecies: 'dog',
      professionalType: 'Walker',
      professionalName: 'Active Paws Walking Service',
      professionalContact: '+1 (555) 456-7890',
      service: 'Daily Walk',
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 15, 0),
      duration: 45,
      status: 'completed',
      location: 'Central Park',
      notes: 'Regular afternoon walk',
      paymentStatus: 'paid',
      createdAt: new Date()
    }
  ]);

  const addAppointment = (appointment: AppointmentData) => {
    setAppointments(prev => [...prev, appointment]);
  };

  const updateAppointment = (id: string, updates: Partial<AppointmentData>) => {
    setAppointments(prev =>
      prev.map(apt => (apt.id === id ? { ...apt, ...updates } : apt))
    );
  };

  const cancelAppointment = (id: string) => {
    setAppointments(prev =>
      prev.map(apt => (apt.id === id ? { ...apt, status: 'cancelled' as const } : apt))
    );
  };

  const getAppointmentsByPet = (petId: string) => {
    return appointments.filter(apt => apt.petId === petId);
  };

  const getAllAppointments = () => {
    return appointments;
  };

  const getUpcomingAppointments = () => {
    const now = new Date();
    return appointments
      .filter(apt => apt.date >= now && apt.status !== 'cancelled' && apt.status !== 'completed')
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  return (
    <AppointmentsContext.Provider
      value={{
        appointments,
        addAppointment,
        updateAppointment,
        cancelAppointment,
        getAppointmentsByPet,
        getAllAppointments,
        getUpcomingAppointments
      }}
    >
      {children}
    </AppointmentsContext.Provider>
  );
}

export function useAppointments() {
  const context = useContext(AppointmentsContext);
  if (!context) {
    throw new Error('useAppointments must be used within AppointmentsProvider');
  }
  return context;
}
